/**
 * PairConfirmCard / CollisionDisambiguation slot rendered above the
 * web notification list when MainPage mounts post-pair. Owns the
 * visibility decision: read `pairAutoNameResult` +
 * `pairConfirmDismissedAt` from `webStorage` (idb-keyval-backed) and
 * render the appropriate component when the gates pass.
 *
 * Visibility gates (mirrors the Android slot in MainScreen.kt and the
 * extension `PairConfirmCardSlot`):
 *   - `pairAutoNameResult` is set, AND
 *   - `pairConfirmDismissedAt` is null/undefined, AND
 *   - `Date.now() - pairAutoNameResult.createdAt < 7 * 24 * 60 * 60 * 1000`.
 *
 * `collision === true` routes to the disambiguation prompt; the
 * confirm card is reserved for the no-collision case.
 */

import { PAIR_CONFIRM_WINDOW_MS, type ReflectoStorage } from "@reflecto/client-core";
import { webStorage } from "../adapters/storage.web";
import { renameDevice } from "../api/client";
import { useStorage } from "../hooks/useStorage";
import { logError } from "../lib/log";
import { CollisionDisambiguation } from "./CollisionDisambiguation";
import { PairConfirmCard } from "./PairConfirmCard";

type AutoNameResult = NonNullable<ReflectoStorage["pairAutoNameResult"]>;

export function PairConfirmCardSlot() {
  // Gate on BOTH keys' hydration. Reading them via two separate `useStorage` calls means
  // they hydrate independently — if `pairAutoNameResult` resolves before
  // `pairConfirmDismissedAt`, a previously-dismissed card would briefly flash visible
  // until the second key catches up. Holding render until both are hydrated removes that
  // flicker. (The extension slot reads both keys atomically in one getStorage call.)
  const { value: result, hydrated: resultHydrated } = useStorage("pairAutoNameResult");
  const { value: dismissedAt, hydrated: dismissedHydrated } = useStorage("pairConfirmDismissedAt");

  if (!resultHydrated || !dismissedHydrated) return null;
  const visible = visibleResult(result, dismissedAt);
  if (!visible) return null;

  if (visible.collision) {
    return (
      <CollisionDisambiguation
        originalName={visible.name}
        onSave={async (name) => {
          await renameAndClear(name, visible.name);
        }}
        onUseAnyway={async () => {
          await dismissConfirm();
        }}
      />
    );
  }

  return (
    <PairConfirmCard
      autoName={visible.name}
      onLooksGood={() => {
        void dismissConfirm();
      }}
      onRename={async (newName) => {
        await renameAndClear(newName, visible.name);
      }}
    />
  );
}

export function visibleResult(
  result: AutoNameResult | undefined,
  dismissedAt: number | undefined
): AutoNameResult | null {
  if (!result) return null;
  // `!= null` (not truthy check): `dismissedAt === 0` is theoretically valid (epoch origin)
  // and would be falsy. Date.now() makes this defensive in practice, but the intent is
  // "dismissed at any timestamp" — the explicit null/undefined check expresses that.
  if (dismissedAt != null) return null;
  if (Date.now() - result.createdAt >= PAIR_CONFIRM_WINDOW_MS) return null;
  return result;
}

async function renameAndClear(newName: string, currentName: string): Promise<void> {
  const trimmed = newName.trim();
  if (!trimmed) return;
  const { token, deviceId } = await webStorage.get("token", "deviceId");
  if (!token || !deviceId) {
    // No-op when credentials are missing — the next pair attempt
    // rewrites pairAutoNameResult from scratch. Still clear the slot
    // so the user isn't stuck.
    await dismissConfirm();
    return;
  }
  if (trimmed !== currentName) {
    try {
      await renameDevice(token, deviceId, trimmed);
      await webStorage.set({ selfDeviceName: trimmed });
    } catch (err) {
      // Re-throw so the calling component (PairConfirmRenameDialog)
      // can flip its in-flight error flag — the slot's own state
      // can't surface a per-input error message.
      logError("pairing", "pair_confirm_rename_failed", err);
      throw err;
    }
  }
  await dismissConfirm();
}

async function dismissConfirm(): Promise<void> {
  await webStorage.set({ pairConfirmDismissedAt: Date.now() });
  await webStorage.remove("pairAutoNameResult");
}
